<template>
  <div class="glow-background">
    <!-- Animated gradient orbs - GPU accelerated -->
    <div class="orb orb-1" />
    <div class="orb orb-2" />
    <div class="orb orb-3" />

    <!-- Floating particles -->
    <div class="particles">
      <div
        v-for="i in 12"
        :key="i"
        class="particle"
        :style="getParticleStyle(i)"
      />
    </div>

    <!-- Subtle scan line -->
    <div class="scan-line" />

    <!-- Geometric accents -->
    <div class="geo-accents">
      <div class="geo-ring geo-ring-1" />
      <div class="geo-ring geo-ring-2" />
      <div class="geo-line geo-line-1" />
      <div class="geo-line geo-line-2" />
    </div>

    <!-- Subtle grid overlay -->
    <div class="grid-overlay" />

    <!-- Soft vignette -->
    <div class="vignette" />
  </div>
</template>

<script setup lang="ts">
const particlePositions = [
  { left: '10%', delay: '0s', duration: '15s', size: '3px' },
  { left: '20%', delay: '2s', duration: '18s', size: '2px' },
  { left: '30%', delay: '5s', duration: '20s', size: '4px' },
  { left: '45%', delay: '1s', duration: '16s', size: '2px' },
  { left: '55%', delay: '4s', duration: '22s', size: '3px' },
  { left: '65%', delay: '3s', duration: '17s', size: '2px' },
  { left: '75%', delay: '6s', duration: '19s', size: '3px' },
  { left: '85%', delay: '2s', duration: '21s', size: '2px' },
  { left: '15%', delay: '7s', duration: '18s', size: '2px' },
  { left: '40%', delay: '4s', duration: '16s', size: '3px' },
  { left: '60%', delay: '8s', duration: '20s', size: '2px' },
  { left: '90%', delay: '1s', duration: '17s', size: '3px' }
] as const

function getParticleStyle(index: number) {
  const p = particlePositions[(index - 1) % particlePositions.length]!
  return {
    left: p.left,
    animationDelay: p.delay,
    animationDuration: p.duration,
    width: p.size,
    height: p.size
  }
}
</script>

<style scoped>
.glow-background {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

/* Floating gradient orbs - uses transform only for GPU acceleration */
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  will-change: transform;
}

.orb-1 {
  width: 50vmax;
  height: 50vmax;
  background: radial-gradient(circle, rgba(0, 200, 255, 0.4) 0%, transparent 70%);
  top: -20%;
  left: -10%;
  animation: float-1 20s ease-in-out infinite;
}

.orb-2 {
  width: 40vmax;
  height: 40vmax;
  background: radial-gradient(circle, rgba(138, 43, 226, 0.35) 0%, transparent 70%);
  bottom: -15%;
  right: -10%;
  animation: float-2 25s ease-in-out infinite;
}

.orb-3 {
  width: 35vmax;
  height: 35vmax;
  background: radial-gradient(circle, rgba(0, 255, 200, 0.25) 0%, transparent 70%);
  top: 40%;
  left: 50%;
  animation: float-3 22s ease-in-out infinite;
}

@keyframes float-1 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(10%, 15%) scale(1.05);
  }
  66% {
    transform: translate(-5%, 10%) scale(0.95);
  }
}

@keyframes float-2 {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(-15%, -10%) scale(1.1);
  }
  66% {
    transform: translate(10%, -5%) scale(0.9);
  }
}

@keyframes float-3 {
  0%, 100% {
    transform: translate(-50%, 0) scale(1);
    opacity: 0.8;
  }
  50% {
    transform: translate(-50%, -20%) scale(1.15);
    opacity: 1;
  }
}

/* Floating particles */
.particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.particle {
  position: absolute;
  bottom: -10px;
  background: white;
  border-radius: 50%;
  opacity: 0;
  animation: particle-rise linear infinite;
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.8);
}

@keyframes particle-rise {
  0% {
    transform: translateY(0) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 0.6;
    transform: translateY(-10vh) scale(1);
  }
  90% {
    opacity: 0.4;
  }
  100% {
    transform: translateY(-110vh) scale(0.5);
    opacity: 0;
  }
}

/* Scan line */
.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.6), rgba(0, 200, 255, 0.6), transparent);
  opacity: 0;
  animation: scan 8s ease-in-out infinite;
  box-shadow: 0 0 20px rgba(168, 85, 247, 0.5);
}

@keyframes scan {
  0%, 100% {
    top: -5%;
    opacity: 0;
  }
  5% {
    opacity: 0.8;
  }
  95% {
    opacity: 0.8;
  }
  100% {
    top: 105%;
    opacity: 0;
  }
}

/* Geometric accents */
.geo-accents {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.geo-ring {
  position: absolute;
  border: 1px solid rgba(168, 85, 247, 0.2);
  border-radius: 50%;
  animation: ring-pulse 6s ease-in-out infinite;
}

.geo-ring-1 {
  width: 300px;
  height: 300px;
  top: 10%;
  right: -100px;
  animation-delay: 0s;
}

.geo-ring-2 {
  width: 200px;
  height: 200px;
  bottom: 15%;
  left: -50px;
  animation-delay: 3s;
}

@keyframes ring-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.6;
  }
}

.geo-line {
  position: absolute;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 200, 255, 0.3), transparent);
  animation: line-glow 4s ease-in-out infinite;
}

.geo-line-1 {
  width: 200px;
  top: 25%;
  left: 5%;
  transform: rotate(-15deg);
  animation-delay: 0s;
}

.geo-line-2 {
  width: 150px;
  bottom: 30%;
  right: 10%;
  transform: rotate(20deg);
  animation-delay: 2s;
}

@keyframes line-glow {
  0%, 100% {
    opacity: 0.2;
  }
  50% {
    opacity: 0.5;
  }
}

/* Subtle grid pattern - static, no animation */
.grid-overlay {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  opacity: 0.5;
}

/* Vignette effect for depth */
.vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.4) 100%);
  pointer-events: none;
}

/* Reduce motion for users who prefer it */
@media (prefers-reduced-motion: reduce) {
  .orb,
  .particle,
  .scan-line,
  .geo-ring,
  .geo-line {
    animation: none;
  }

  .particle {
    display: none;
  }
}

/* Slightly smaller orbs on mobile for better performance */
@media (max-width: 768px) {
  .orb {
    filter: blur(60px);
  }

  .orb-1 {
    width: 40vmax;
    height: 40vmax;
  }

  .orb-2 {
    width: 35vmax;
    height: 35vmax;
  }

  .orb-3 {
    width: 30vmax;
    height: 30vmax;
  }

  .geo-ring-1 {
    width: 200px;
    height: 200px;
  }

  .geo-ring-2 {
    width: 150px;
    height: 150px;
  }

  .geo-line-1,
  .geo-line-2 {
    display: none;
  }
}
</style>
